import { z } from 'zod';
import { VISIT_SORTS, VISIT_STATUSES } from '@itvisits/shared';
import { DEFAULT_SEARCH, type VisitSearch } from './VisitFilters';

const statusList = z.preprocess(
  (v) => (typeof v === 'string' ? v.split(',').filter(Boolean) : Array.isArray(v) ? v : []),
  z.array(z.enum(VISIT_STATUSES)),
);

/** URL search schema for the visits routes (constitution §5.6). Unknown values fall back to defaults. */
export const visitSearchSchema = z.object({
  status: statusList.catch([]),
  technicianId: z.coerce.number().int().positive().optional().catch(undefined),
  facilityId: z.coerce.number().int().positive().optional().catch(undefined),
  clusterId: z.coerce.number().int().positive().optional().catch(undefined),
  from: z.iso.date().optional().catch(undefined),
  to: z.iso.date().optional().catch(undefined),
  wip: z.boolean().catch(DEFAULT_SEARCH.wip),
  q: z.string().max(120).catch(''),
  sort: z.enum(VISIT_SORTS).catch('scheduled_desc'),
  page: z.coerce.number().int().min(1).catch(1),
  /** Deep link from emails and invitations: opens this visit's details on load. */
  open: z.coerce.number().int().positive().optional().catch(undefined),
});

export type VisitSearchParams = z.infer<typeof visitSearchSchema>;

export const DEFAULT_PARAMS: VisitSearchParams = {
  status: [],
  wip: DEFAULT_SEARCH.wip,
  q: '',
  sort: 'scheduled_desc',
  page: 1,
};

/** Full search object for navigate(); optional keys are omitted when unset so URLs stay short. */
export function toSearchParams(s: VisitSearch): VisitSearchParams {
  return {
    status: s.status,
    wip: s.wip,
    q: s.q,
    sort: s.sort,
    page: s.page,
    ...(s.technicianId ? { technicianId: s.technicianId } : {}),
    ...(s.facilityId ? { facilityId: s.facilityId } : {}),
    ...(s.clusterId ? { clusterId: s.clusterId } : {}),
    ...(s.from ? { from: s.from } : {}),
    ...(s.to ? { to: s.to } : {}),
    ...(s.open ? { open: s.open } : {}),
  };
}

export function fromSearchParams(p: VisitSearchParams): VisitSearch {
  return {
    status: p.status,
    technicianId: p.technicianId,
    facilityId: p.facilityId,
    clusterId: p.clusterId,
    from: p.from,
    to: p.to,
    wip: p.wip,
    q: p.q,
    sort: p.sort,
    page: p.page,
    open: p.open,
  };
}
